import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';

const alertVariants = cva(
  'rounded-xl border p-4',
  {
    variants: {
      variant: {
        success: 'border-success-500 bg-success-50 dark:border-success-500/30 dark:bg-success-500/15',
        error: 'border-error-500 bg-error-50 dark:border-error-500/30 dark:bg-error-500/15',
        warning: 'border-warning-500 bg-warning-50 dark:border-warning-500/30 dark:bg-warning-500/15',
        info: 'border-blue-light-500 bg-blue-light-50 dark:border-blue-light-500/30 dark:bg-blue-light-500/15',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  }
);

const alertIconVariants = cva('flex-shrink-0', {
  variants: {
    variant: {
      success: 'text-success-500',
      error: 'text-error-500',
      warning: 'text-warning-500',
      info: 'text-blue-light-500',
    },
  },
});

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  icon?: React.ReactNode;
  title?: string;
  action?: React.ReactNode;
  onClose?: () => void;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant, icon, title, action, onClose, children, ...props }, ref) => {
    return (
      <div
        role="alert"
        className={cn(alertVariants({ variant, className }))}
        ref={ref}
        {...props}
      >
        <div className="flex items-start gap-3">
          {icon && (
            <div className={cn(alertIconVariants({ variant }), '-mt-0.5')}>
              {icon}
            </div>
          )}
          <div className="flex-1">
            {title && (
              <h4 className="mb-1 text-sm font-semibold text-gray-800 dark:text-white/90">
                {title}
              </h4>
            )}
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {children}
            </div>
            {action && <div className="mt-3">{action}</div>}
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Close alert"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  }
);

Alert.displayName = 'Alert';
